import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Logo, LogoMark } from "@/components/Logo";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { ArrowRight, Globe } from "lucide-react";

const RIGHT_IMG = "https://images.unsplash.com/photo-1589936009537-bda4341d578f?w=1400&q=80";

function Shell({ title, desc, children, footer, testid }) {
  const { t, lang, setLang } = useLang();
  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-[#F2F2F2]" data-testid={testid}>
      <div className="flex flex-col p-8 md:p-14">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="auth-home"><Logo /></Link>
          <button
            onClick={() => setLang(lang === "tr" ? "en" : "tr")}
            className="flex items-center gap-1.5 text-[0.72rem] uppercase tracking-[0.18em] font-mono-aq"
            data-testid="auth-lang-switcher"
          >
            <Globe size={14} /> {lang.toUpperCase()}
          </button>
        </div>
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto">
            <h1 className="font-serif-display text-4xl md:text-5xl tracking-tighter leading-tight">{title}</h1>
            {desc && <p className="mt-4 text-[#4A4A4A]">{desc}</p>}
            <div className="mt-10">{children}</div>
            <div className="mt-8 text-sm text-[#4A4A4A]">{footer}</div>
          </div>
        </div>
        <div className="font-mono-aq text-[0.65rem] tracking-[0.22em] uppercase text-[#B6B09F]">© Aqion</div>
      </div>
      <div className="hidden md:block relative bg-[#0A0A0A] overflow-hidden">
        <img src={RIGHT_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 grayscale" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-[#EAE4D5]">
          <LogoMark size={56} />
          <div>
            <div className="label-eyebrow text-[#B6B09F]">— {t.auth.side_eyebrow}</div>
            <p className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight max-w-md">
              {t.auth.side_text}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required, minLength }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <input
        type={type}
        value={value}
        required={required}
        minLength={minLength}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-[#B6B09F] focus:border-[#1E3A5F] outline-none py-3 text-base text-[#0A0A0A] transition-colors"
        data-testid={testid}
      />
    </div>
  );
}

export function ForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success(t.auth.forgot_sent);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <Shell
      title={t.auth.forgot_title}
      desc={t.auth.forgot_desc}
      testid="forgot-password-page"
      footer={<Link to="/login" className="aq-link font-medium">← {t.auth.to_login}</Link>}
    >
      {sent ? (
        <div className="aq-card p-6">
          <p className="text-sm leading-relaxed">{t.auth.forgot_sent}</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-6" data-testid="forgot-form">
          <Field label={t.auth.email} type="email" value={email} onChange={setEmail} required testid="forgot-email" />
          <button type="submit" disabled={loading} className="aq-button-primary w-full" data-testid="forgot-submit">
            {loading ? "..." : t.auth.forgot_submit} <ArrowRight size={14} />
          </button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const { t } = useLang();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success(t.auth.reset_success);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <Shell
      title={t.auth.reset_title}
      desc={t.auth.reset_desc}
      testid="reset-password-page"
      footer={<Link to="/login" className="aq-link font-medium">← {t.auth.to_login}</Link>}
    >
      {done ? (
        <div className="aq-card p-6">
          <p className="text-sm leading-relaxed mb-4">{t.auth.reset_success}</p>
          <Link to="/login" className="aq-button-primary inline-flex" data-testid="reset-back-login">
            {t.auth.login_btn} <ArrowRight size={14} />
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-6" data-testid="reset-form">
          <Field label={t.auth.password} type="password" value={password} onChange={setPassword} required minLength={6} testid="reset-password-input" />
          <button type="submit" disabled={loading || !token} className="aq-button-primary w-full" data-testid="reset-submit">
            {loading ? "..." : t.auth.reset_submit} <ArrowRight size={14} />
          </button>
        </form>
      )}
    </Shell>
  );
}
